import { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { publicService } from "../services/publicService";

const LEVEL_CONFIG = {
	Expert: {
		label: "Expert",
		bg: "bg-green-100",
		text: "text-green-800",
		border: "border-green-200",
		dot: "bg-green-500",
	},
	Intermediate: {
		label: "Intermediate",
		bg: "bg-yellow-100",
		text: "text-yellow-800",
		border: "border-yellow-200",
		dot: "bg-yellow-500",
	},
	Beginner: {
		label: "Beginner",
		bg: "bg-orange-100",
		text: "text-orange-800",
		border: "border-orange-200",
		dot: "bg-orange-400",
	},
	NONE: {
		label: "Not Rated",
		bg: "bg-gray-50",
		text: "text-gray-300",
		border: "border-gray-100",
		dot: "bg-gray-200",
	},
};

export default function SkillMatrix() {
	const [resources, setResources] = useState([]);
	const [categories, setCategories] = useState([]);
	const [activeCategory, setActiveCategory] = useState("");
	const [loading, setLoading] = useState(true);

	const competencyRank = {
		Beginner: 1,
		Intermediate: 2,
		Expert: 3,
	};

	const getCategorySkillCompetency = (resource, skillName) => {
		const matches = (resource?.categories || []).flatMap((category) =>
			(category?.skills || [])
				.filter((skill) => skill?.name === skillName)
				.map((skill) => skill?.competency)
				.filter(Boolean),
		);

		if (!matches.length) {
			return "NONE";
		}

		return matches.reduce((best, current) => {
			const bestRank = competencyRank[best] || 0;
			const currentRank = competencyRank[current] || 0;
			return currentRank > bestRank ? current : best;
		});
	};

	const getSkillLevel = (resource, skillName) => {
		const generalLevel = resource?.skills?.[skillName];
		if (generalLevel && generalLevel !== "None") {
			return generalLevel;
		}

		return getCategorySkillCompetency(resource, skillName);
	};

	useEffect(() => {
		Promise.all([publicService.getCategories(), publicService.getSkillMatrix()])
			.then(([categoriesData, matrixData]) => {
				const categoryNames = (categoriesData || []).map((c) => c.name);
				setCategories(categoryNames);
				setActiveCategory((prev) => prev || categoryNames[0] || "");
				setResources(matrixData || []);
			})
			.finally(() => setLoading(false));
	}, []);

	useEffect(() => {
		if (!activeCategory) return;

		setLoading(true);
		publicService
			.getSkillMatrix({ category: activeCategory })
			.then((data) => {
				setResources(data || []);
			})
			.finally(() => setLoading(false));
	}, [activeCategory]);

	const skills = useMemo(() => {
		const allSkills = new Set();

		resources.forEach((resource) => {
			Object.entries(resource.skills || {}).forEach(([skillName, level]) => {
				if (level && level !== "None") {
					allSkills.add(skillName);
				}
			});

			(resource.categories || []).forEach((category) => {
				(category.skills || []).forEach((skill) => {
					if (skill?.name && skill?.competency && skill.competency !== "None") {
						allSkills.add(skill.name);
					}
				});
			});
		});

		return Array.from(allSkills).sort();
	}, [resources]);

	if (loading) {
		return (
			<div className="min-h-screen flex flex-col bg-gray-50">
				<Navbar />
				<main className="flex-1 flex items-center justify-center py-24">
					<div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
				</main>
				<Footer />
			</div>
		);
	}

	return (
		<div className="min-h-screen flex flex-col bg-gray-50">
			<Navbar />
			<main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
				<div className="mb-8">
					<h1 className="text-3xl font-bold text-gray-900 mb-2">
						Skill Matrix
					</h1>
					<p className="text-gray-600">
						Category-wise matrix with resources as rows and skills as columns
					</p>
				</div>

				<div className="flex flex-wrap gap-4 mb-6 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
					<span className="text-sm font-semibold text-gray-700 mr-2">
						Legend:
					</span>
					{Object.entries(LEVEL_CONFIG)
						.filter(([key]) => key !== "NONE")
						.map(([, cfg]) => (
							<div key={cfg.label} className="flex items-center gap-1.5">
								<span className={`w-3 h-3 rounded-full ${cfg.dot}`}></span>
								<span className="text-sm text-gray-700">{cfg.label}</span>
							</div>
						))}
					<div className="flex items-center gap-1.5">
						<span className="w-3 h-3 rounded-full bg-gray-200"></span>
						<span className="text-sm text-gray-500">Not Rated</span>
					</div>
				</div>

				<div className="flex flex-wrap gap-2 mb-6">
					{categories.map((category) => (
						<button
							key={category}
							onClick={() => setActiveCategory(category)}
							className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
								activeCategory === category
									? "bg-primary-600 text-white shadow-sm"
									: "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
							}`}
						>
							{category}
						</button>
					))}
				</div>

				<div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
					{resources.length === 0 ? (
						<div className="p-12 text-center text-gray-500">
							No resources found for this category
						</div>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full text-sm">
								<thead>
									<tr className="bg-gray-50 border-b border-gray-200">
										<th className="text-left px-4 py-3 font-semibold text-gray-700 sticky left-0 bg-gray-50 min-w-[150px]">
											Resource
										</th>
										{skills.map((skill) => (
											<th
												key={skill}
												className="text-center px-3 py-3 font-semibold text-gray-700 min-w-[130px]"
											>
												{skill}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{resources.map((resource, idx) => (
										<tr
											key={resource.id}
											className={idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}
										>
											<td
												className={`px-4 py-2.5 font-medium text-gray-800 sticky left-0 ${
													idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
												} border-r border-gray-100`}
											>
												<div className="text-xs text-gray-400 font-normal">
													ID-{resource.id}
												</div>
												<div className="text-sm">{resource.role}</div>
											</td>
											{skills.map((skill) => {
												const level = getSkillLevel(resource, skill);
												const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG.NONE;

												return (
													<td
														key={`${resource.id}-${skill}`}
														className="text-center px-3 py-2.5"
													>
														<span
															className={`inline-flex items-center justify-center w-full py-1 px-2 rounded text-xs font-medium ${cfg.bg} ${cfg.text} border ${cfg.border}`}
														>
															{cfg.label}
														</span>
													</td>
												);
											})}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</main>
			<Footer />
		</div>
	);
}
